<template>
    <v-combobox
        ref="autocomplete"
        v-model="selected"
        :label="label"
        :placeholder="placeholder"
        :searchInput.sync="senderSearchStr"
        :items="senderRecords"
        :loading="inFlight"
        :rules="rules"
        :hideNoData="( !senderSearchStr && allowCreate ) || inFlight"
        autoSelectFirst
        clearable
        dense
        hideDetails
        noFilter
        outlined
        @click:clear="clearButtonHandler()"
        @keydown.enter="enterHandler()"
    >
        <template
            v-slot:no-data
            v-if="allowCreate"
        >
            <v-list-item>
                <span class="subtitle-1">
                    <strong>Create:</strong> {{ createValidationText }}
                </span>
            </v-list-item>
            <v-list-item>
                <span class="subtitle-2">Hit 'Enter' to confirm</span>
            </v-list-item>
        </template>
    </v-combobox>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Packages
import _ from 'lodash';

// Api
import LogisticsApi from '@/api/LogisticsApi';

export default {
    name: 'AutocompleteSuggestSender',
    props: {
        value: {
            type: String,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: 'Sender',
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Start typing to Search',
        },
        rules: {
            type: Array,
            required: false,
            default: () => ( [] ),
        },
        // Allow adding user-typed values to senderRecords
        allowCreate: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            /** @type { Boolean } */
            inFlight: false,
            /** @type { Object[] } */
            senderRecords: [],
            /** @type { Null | DebouncedFunc | Function } */
            searchSendersDebounced: null,
            /** @type { String } */
            senderSearchStr: '',
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
        } ),
        /** @returns { Null | String } */
        selected: {
            get() {
                return this.value;
            },
            set( value ) {
                this.$emit( 'input', value ?? null );
            },
        },

        /** @returns { String } */
        createValidationText() {
            return typeof this.senderSearchStr === 'string'
                ? this.senderSearchStr.trim().toUpperCase()
                : '';
        },
    },
    watch: {
        senderSearchStr() {
            if ( this.senderSearchStr && this.senderSearchStr !== this.selected ) {
                this.searchSendersDebounced();
            }
        },
        value() {
            // Clear out old results when we null out the value from the parent
            if ( !this.value ) {
                this.senderRecords = [];
            }
        },
    },
    created() {
        this.searchSendersDebounced = _.debounce( () => { this.getSenderSuggestions(); }, 500 );
    },
    mounted() {
        if ( !!this.value && ( this.senderRecords.length === 0 ) ) {
            this.senderRecords = [ this.value ];
        }
    },
    methods: {
        async getSenderSuggestions() {
            this.inFlight = true;
            try {
                const { data } = await LogisticsApi.getAutoSuggestSender( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    searchStr: this.senderSearchStr,
                } );

                this.senderRecords = data.stringList || [];
            } catch ( error ) {
                console.error( 'ERROR getting sender suggestions: ', error );
            } finally {
                this.inFlight = false;
            }
        },
        clearButtonHandler() {
            this.senderRecords = [];
            this.selected = null;
            this.senderSearchStr = '';
        },
        enterHandler() {
            if ( this.allowCreate ) {
                // Add user input to list
                if ( !this.senderRecords.includes( this.createValidationText ) ) {
                    this.senderRecords.push( this.createValidationText );
                }
                // Emit selection
                this.selected = this.senderSearchStr;
            }
        },
    },
};
</script>
